import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams, useParams } from 'react-router-dom';

import { useAppBranding } from '../../../config/branding';
import DefaultApiLimit from '../../../config/defaultLimit';
import { RevenueByMonthInterFace } from '../../../interface/by-month-revenue.interface';
import { useAppDispatch, useAppSelector } from '../../../redux/hooks';
import {
  disableByMonthFilterApplied,
  fetchRevenueByMonth,
  fetchRevenueByMonthFilters,
  setSortData,
} from '../../../redux/slices/ByMonthRevenueSlice';
import { setNotificationMessage } from '../../../redux/slices/NotificationManagementSlice';
import CustomTable from '../../../shared-components/custom-table/CustomTable';
import { GetParametersFromCommonFilters } from '../../../shared-functions/CommonFilters';
import { exportCSV, exportPDF } from '../../../shared-functions/ExportFile';
import GetMonthName from '../../../shared-functions/GetMonthName';
import ToFixedValue from '../../../shared-functions/ToFixedValue';
import accountApi from '../../../utils/api/account';

/**
 * By transaction data table component
 * @returns {any} The result
 */
function RevenueByMonthAccount() {
  const branding = useAppBranding();
  const { t, i18n } = useTranslation(['account', 'userRole']);
  const dispatch = useAppDispatch();
  const [searchParameters] = useSearchParams();
  const { id } = useParams<{ id: string }>();
  const accountCustomId = id || searchParameters.get('accountCustomId');
  const {
    RevenueByMonth,
    limit,
    page,
    totalPages,
    totalResults,
    loading,
    filters,
    filterListLoading,
    isFilterUpdated,
    sortBy,
    sortField,
  } = useAppSelector((state) => state.revenueByMonth);
  const [exportLoader, setExportLoader] = useState<boolean>(false);
  const [hiddenColumns, setHiddenColumns] = useState<string[]>([]);

  const column = [
    {
      label: t('month'),
      accessorKey: 'month' as keyof RevenueByMonthInterFace,
      /**
       * Rendering month name only
       * @param {any} byMonthData - The byMonthData
       * @returns {any} The result
       */
      customData: (byMonthData: RevenueByMonthInterFace) => (
        <p>{GetMonthName({ timestamp: byMonthData.month })}</p>
      ),
    },
    {
      label: t('totalAmount'),
      accessorKey: 'totalAmount' as keyof RevenueByMonthInterFace,
      sortAllow: true,
      /**
       * Amount with two decimal
       * @param {any} byMonth - The byMonth
       * @returns {any} The result
       */
      customData: (byMonth: RevenueByMonthInterFace) => (
        <div className="value-container">
          <p className="value">{ToFixedValue(byMonth.totalAmount)}</p>
          <span>{branding.text.currency}</span>
        </div>
      ),
    },
    {
      label: t('amount'),
      accessorKey: 'totalRevenue' as keyof RevenueByMonthInterFace,
      sortAllow: true,
      /**
       * Amount with two decimal
       * @param {any} byMonth - The byMonth
       * @returns {any} The result
       */
      customData: (byMonth: RevenueByMonthInterFace) => (
        <div className="value-container">
          <p className="value">{ToFixedValue(byMonth.totalRevenue)}</p>
          <span>{branding.text.currency}</span>
        </div>
      ),
    },
    {
      label: t('powerConsumed'),
      accessorKey: 'powerConsumed' as keyof RevenueByMonthInterFace,
      sortAllow: true,
      /**
       * Power consumed with two decimal
       * @param {any} byMonth - The byMonth
       * @returns {any} The result
       */
      customData: (byMonth: RevenueByMonthInterFace) => {
        return <p>{ToFixedValue(byMonth?.powerConsumed, 3)} kWh</p>;
      },
    },
    {
      label: t('adminShare'),
      accessorKey: 'totalAdminRevenue' as keyof RevenueByMonthInterFace,
      sortAllow: true,
      /**
       * Admin's Share with two decimal
       * @param {any} byMonth - The byMonth
       * @returns {any} The result
       */
      customData: (byMonth: RevenueByMonthInterFace) => (
        <div className="value-container">
          <p className="value">{ToFixedValue(byMonth.totalAdminRevenue)}</p>
          <span>{branding.text.currency}</span>
        </div>
      ),
    },
    {
      label: t('adminSharePercent'),
      accessorKey: 'adminRevenuePercentage' as keyof RevenueByMonthInterFace,
      sortAllow: true,
      /**
       * Admin share percentage with two decimal
       * @param {any} byMonth - The byMonth
       * @returns {any} The result
       */
      customData: (byMonth: RevenueByMonthInterFace) => (
        <div className="value-container">
          <p className="value">{ToFixedValue(byMonth.adminRevenuePercentage)}</p>
          <span>%</span>
        </div>
      ),
    },
    {
      label: t('facilityShare'),
      accessorKey: 'totalFoRevenue' as keyof RevenueByMonthInterFace,
      sortAllow: true,
      /**
       * Facility share with two decimal
       * @param {any} byMonth - The byMonth
       * @returns {any} The result
       */
      customData: (byMonth: RevenueByMonthInterFace) => (
        <div className="value-container">
          <p className="value">{ToFixedValue(byMonth.totalFoRevenue)}</p>
          <span>{branding.text.currency}</span>
        </div>
      ),
    },
    {
      label: t('facilitySharePercent'),
      accessorKey: 'foRevenuePercentage' as keyof RevenueByMonthInterFace,
      sortAllow: true,
      /**
       * Facility share percentage with two decimal
       * @param {any} byMonth - The byMonth
       * @returns {any} The result
       */
      customData: (byMonth: RevenueByMonthInterFace) => (
        <div className="value-container">
          <p className="value">{ToFixedValue(byMonth.foRevenuePercentage)}</p>
          <span>%</span>
        </div>
      ),
    },
    {
      label: t('idleFee'),
      accessorKey: 'totalIdleFee' as keyof RevenueByMonthInterFace,
      sortAllow: true,
      /**
       * FO revenue percentage with two decimal
       * @param {any} byMonth - The byMonth
       * @returns {any} The result
       */
      customData: (byMonth: RevenueByMonthInterFace) => <p>{ToFixedValue(byMonth.totalIdleFee)}</p>,
    },
    {
      label: t('platformFee'),
      accessorKey: 'totalPlatformFee' as keyof RevenueByMonthInterFace,
      sortAllow: true,
      /**
       * FO revenue percentage with two decimal
       * @param {any} byMonth - The byMonth
       * @returns {any} The result
       */
      customData: (byMonth: RevenueByMonthInterFace) => (
        <div className="value-container">
          <p className="value">{ToFixedValue(byMonth.totalPlatformFee)}</p>
          <span>{branding.text.currency}</span>
        </div>
      ),
    },
    {
      label: t('sessionFee'),
      accessorKey: 'totalSessionFee' as keyof RevenueByMonthInterFace,
      sortAllow: true,
      /**
       * FO revenue percentage with two decimal
       * @param {any} byMonth - The byMonth
       * @returns {any} The result
       */
      customData: (byMonth: RevenueByMonthInterFace) => (
        <div className="value-container">
          <p className="value">{ToFixedValue(byMonth.totalSessionFee)}</p>
          <span>{branding.text.currency}</span>
        </div>
      ),
    },
  ];

  /**
   * Fetch Users list function
   * @param {any} data - The data
   */
  const fetchData = (data: any) => {
    if (!loading && accountCustomId) {
      dispatch(fetchRevenueByMonth({ accountCustomId, ...data }));
    }
  };
  /**
   * Table pagination function
   * @param {any} value Table pagination function
   */
  const pageNateLimitChange = (value: any) => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    fetchData({
      ...queryParameters,
      ...value,
      sortField,
      sortBy,
    });
  };

  /**
   * Export data function
   * @param {any} type - The type
   */
  const exportDataPdfOrCSV = (type: string) => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    if (!accountCustomId) return;
    setExportLoader(true);
    accountApi
      .fetchByMonthExportData(accountCustomId, queryParameters)
      .then((response) => {
        // Convert data to CSV format
        // const headers = [
        //   t('sNo'),
        //   t('month'),
        //   t('amount'),
        //   t('powerConsumed'),
        //   t('adminShare'),
        //   t('adminSharePercent'),
        //   t('facilityShare'),
        //   t('facilitySharePercent'),
        //   t('sessionFee'),
        // ];

        const visibleColumns = column.filter(
          (col) => !hiddenColumns.includes(col.accessorKey as string),
        );

        // ✅ headers
        const headers = [t('sNo'), ...visibleColumns.map((col) => col.label)];

        // const rows = response.data.map((row: RevenueByMonthInterFace, index: number) => [
        //   index + 1,
        //   GetMonthName({ timestamp: row.month }),
        //   ToFixedValue(row.totalRevenue),
        //   `${ToFixedValue(Number(row?.powerConsumed) / 1000)} Khw` || '0.00',
        //   ToFixedValue(row.totalAdminRevenue),
        //   ToFixedValue(row.adminRevenuePercentage),
        //   ToFixedValue(row.totalFoRevenue),
        //   ToFixedValue(row.foRevenuePercentage),
        //   ToFixedValue(row.totalSessionFee),
        // ]);

        const currencyColumns = [
          'totalAmount',
          'totalRevenue',
          'totalAdminRevenue',
          'totalFoRevenue',
          'totalPlatformFee',
          'totalSessionFee',
        ];

        // ✅ rows
        const rows = response.data.map((row: RevenueByMonthInterFace, index: number) => [
          index + 1,
          ...visibleColumns.map((col) => {
            const value = row[col.accessorKey];

            if (col.accessorKey === 'month') {
              return GetMonthName({ timestamp: row.month });
            }
            if (col.accessorKey === 'powerConsumed') {
              return ToFixedValue(value, 3);
            }
            if (typeof value === 'number') {
              return currencyColumns.includes(col.accessorKey as string)
                ? `${ToFixedValue(value)} ${branding.text.currency}`
                : ToFixedValue(value);
            }

            return value ?? '';
          }),
        ]);

        setExportLoader(false);
        if (rows.length > 0) {
          if (type === 'csv') {
            exportCSV(headers, rows, 'reports-list.csv');
          } else {
            exportPDF(headers, rows, 'reports-list.pdf');
          }
        }
      })
      .catch((error) => {
        setExportLoader(false);
        dispatch(
          setNotificationMessage({
            message: error?.response?.data?.message || t('somethingWentWrong'),
            status: true,
            type: 'error',
            code: error?.response?.data?.code || 404,
          }),
        );
      });
  };

  /**
   * Handle the filter data clear function
   * @param {any} sortData - The sortData
   */
  const handleSortFunction = (sortData: any) => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    dispatch(setSortData(sortData));
    fetchData({
      ...queryParameters,
      sortBy: sortData.sortBy,
      sortField: sortData.sortField,
      page: DefaultApiLimit.page,
      limit,
    });
  };

  /**
   * Function to refresh data
   */
  const handleRefresh = () => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    fetchData({ ...queryParameters, page, limit, sortBy, sortField });
  };

  useEffect(() => {
    if (isFilterUpdated || RevenueByMonth.length === 0 || accountCustomId) {
      const queryParameters = GetParametersFromCommonFilters(filters);
      fetchData({
        page: DefaultApiLimit.page,
        limit,
        sortField,
        sortBy,
        ...queryParameters,
      });
      dispatch(disableByMonthFilterApplied());
    }
  }, [filters, sortField, sortBy,accountCustomId]);
  return (
    <div dir={i18n.dir()}>
      <CustomTable
        version="v2"
        header={column}
        data={RevenueByMonth}
        sortData={{ sortBy, sortField }}
        handleSortChange={handleSortFunction}
        limit={limit}
        page={page}
        totalPages={totalPages}
        totalResults={totalResults}
        loading={loading}
        serialNo
        pageLimitChange={pageNateLimitChange}
        onHiddenColumnsChange={setHiddenColumns}
        filters={filters}
        filterListLoading={filterListLoading}
        exportDataPdfOrCSV={exportDataPdfOrCSV}
        filterListToDispatch={fetchRevenueByMonthFilters}
        exportLoader={exportLoader}
        exportData={RevenueByMonth}
        refershCurrentPages={handleRefresh}
        refreshRequired
        hoverLabel={t('refresh')}
        sizeChangeButtonRequired
        height={600}
        activeSearchFilter={false}
      />
    </div>
  );
}

export default RevenueByMonthAccount;
